import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ForfeoPartnerNetworkProgram from '../components/network/ForfeoPartnerNetworkProgram';
import { FaStore, FaChartLine, FaQrcode, FaGift, FaUsers, FaArrowRight, FaCheckCircle } from 'react-icons/fa';

const copy = {
  fr: {
    badge: 'Partenaires locaux', title: 'Faites rayonner votre entreprise avec Forfeo.', subtitle: 'Forfeo aide les entreprises locales à présenter leurs offres, forfaits, cartes numériques et expériences à une audience prête à découvrir.',
    cta: 'Inscrire mon entreprise', secondary: 'Voir comment ça marche',
    cards: [
      { icon: 'store', title: 'Visibilité locale', text: 'Présentez vos offres dans un environnement pensé pour la découverte et la conversion.' },
      { icon: 'gift', title: 'Cartes et forfaits', text: 'Créez des forfaits, expériences ou cartes numériques adaptés à votre marque.' },
      { icon: 'qr', title: 'Validation simplifiée', text: 'Utilisez des QR codes et confirmations numériques pour simplifier les opérations.' },
      { icon: 'chart', title: 'Croissance mesurable', text: 'Suivez l’intérêt, les ventes, réservations et opportunités de votre présence numérique.' },
    ],
    promiseTitle: 'Pourquoi rejoindre le réseau Forfeo ?',
    promises: ['Un positionnement local et premium', 'Des outils numériques simples', 'Des opportunités de bundles et collaborations', 'Une expérience client plus fluide', 'Un canal additionnel de visibilité'],
    finalTitle: 'Prêt à devenir partenaire ?', finalText: 'Inscrivez votre entreprise et préparez votre vitrine Forfeo.'
  },
  en: {
    badge: 'Local partners', title: 'Help your business stand out with Forfeo.', subtitle: 'Forfeo helps local businesses present offers, packages, digital cards and experiences to an audience ready to discover.',
    cta: 'Register my business', secondary: 'See how it works',
    cards: [
      { icon: 'store', title: 'Local visibility', text: 'Showcase your offers in an environment designed for discovery and conversion.' },
      { icon: 'gift', title: 'Cards and packages', text: 'Create packages, experiences or digital cards tailored to your brand.' },
      { icon: 'qr', title: 'Simplified validation', text: 'Use QR codes and digital confirmations to simplify operations.' },
      { icon: 'chart', title: 'Measurable growth', text: 'Track interest, sales, reservations and opportunities from your digital presence.' },
    ],
    promiseTitle: 'Why join the Forfeo network?',
    promises: ['A local and premium positioning', 'Simple digital tools', 'Bundle and collaboration opportunities', 'A smoother customer experience', 'An additional visibility channel'],
    finalTitle: 'Ready to become a partner?', finalText: 'Register your business and prepare your Forfeo storefront.'
  }
};

const icons: Record<string, React.ReactNode> = { store: <FaStore />, gift: <FaGift />, qr: <FaQrcode />, chart: <FaChartLine /> };

export default function Partenaires() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const t = copy[lang];

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans">
      <Header />
      <main className="pt-28 pb-24">
        <section className="px-4 py-20 bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-white">
          <div className="max-w-6xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest mb-8"><FaStore /> {t.badge}</div>
            <h1 className="text-5xl md:text-7xl font-black tracking-tight max-w-5xl mb-6">{t.title}</h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-3xl leading-relaxed mb-10">{t.subtitle}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/business" className="px-8 py-4 bg-white text-slate-950 rounded-2xl font-black hover:bg-slate-100 transition flex items-center justify-center gap-3">{t.cta} <FaArrowRight /></Link>
              <Link to="/comment-ca-marche" className="px-8 py-4 bg-white/10 border border-white/10 rounded-2xl font-black hover:bg-white/15 transition">{t.secondary}</Link>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.cards.map((card) => (
              <div key={card.title} className="bg-white border border-slate-100 rounded-3xl p-7 shadow-xl shadow-slate-200/40 hover:-translate-y-1 transition">
                <div className="w-14 h-14 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center text-xl mb-5">{icons[card.icon]}</div>
                <h3 className="text-xl font-black mb-3">{card.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{card.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 py-10">
          <ForfeoPartnerNetworkProgram />
        </section>

        <section className="max-w-6xl mx-auto px-4 py-10">
          <div className="bg-slate-50 border border-slate-100 rounded-[32px] p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <FaUsers className="text-blue-600 text-5xl mb-6" />
              <h2 className="text-3xl md:text-4xl font-black mb-4">{t.promiseTitle}</h2>
              <p className="text-slate-600">{t.subtitle}</p>
            </div>
            <div className="space-y-4">
              {t.promises.map((item) => (
                <div key={item} className="flex gap-3 items-center bg-white rounded-2xl p-4 border border-slate-100">
                  <FaCheckCircle className="text-green-500" />
                  <span className="font-bold text-slate-800">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 text-center py-20">
          <h2 className="text-3xl md:text-4xl font-black mb-4">{t.finalTitle}</h2>
          <p className="text-slate-600 mb-8">{t.finalText}</p>
          <Link to="/business" className="inline-flex items-center gap-3 px-8 py-4 bg-blue-600 text-white rounded-2xl font-black hover:bg-blue-700 transition">{t.cta} <FaArrowRight /></Link>
        </section>
      </main>
      <Footer />
    </div>
  );
}
